import { Typography } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import { Divider } from '@mui/material';
import Box from '@mui/material/Box';
import { Stack } from '@mui/system';
import { ReactElement, useRef } from 'react';
import { Position } from 'reactflow';
import { useResizeObserver } from 'usehooks-ts';

import NodeBackground from './NodeBackground';
import { toTaskDependencyHandleId } from '../../../features/helpers';
import {
  type CodeTaskProperties,
  type DataExportTaskProperties,
  PropertiesType,
} from '../../../types';
import { DataHandleComponentData } from '../../../types/taskNode';
import { FILE_PATH_FIELD } from '../../PropertiesSidebar/ExportTaskSideBar';
import { DataHandleWithLabel } from '../DataHandle';
import Handle from '../Handle';
import { useDataTaskHandleComponents } from '../nodes/TaskNode/utils';

export type Props = {
  properties: DataExportTaskProperties | CodeTaskProperties;
  selected: boolean;
  indicators: ReactElement;
  selectLabel: string;
};

export const getFilteredInputsWithFeatureFlag = (
  inputs: DataHandleComponentData[],
  isDataExportNode: boolean,
  staticFilePathInputEnabledFlag: boolean | undefined
) => {
  return inputs.filter(({ dataKey }) => {
    if (!isDataExportNode) return true; // only filter for data export nodes
    if (!staticFilePathInputEnabledFlag) return true; // no filtering if flag is disabled
    if (dataKey === FILE_PATH_FIELD) return false;
    return true;
  });
};

export const DataNode = ({
  selected,
  properties: { id, nickname, taskType, propertiesType },
  indicators,
  selectLabel,
}: Props) => {
  const dataNodeContentRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver({ ref: dataNodeContentRef });

  const { inputs, outputs } = useDataTaskHandleComponents({
    taskId: id,
    propertiesType,
  });

  const staticFilePathInputEnabled = useFlag('fe.use_static_file_path_input');
  const isDataExportNode = propertiesType === PropertiesType.DataExport;

  const inputsFiltered = getFilteredInputsWithFeatureFlag(
    inputs,
    isDataExportNode,
    staticFilePathInputEnabled
  );

  return (
    <Stack direction="row" pr={2}>
      {/* Label */}
      <Box
        textAlign="right"
        position="absolute"
        left="0"
        sx={{ transform: 'translate(-100%) translate(-0.5rem)' }}
        bgcolor="rgba(248, 248, 248, 0.8)"
        borderRadius={1}
        p={1}
      >
        <Typography variant="h6">{nickname ?? taskType}</Typography>
        <Typography variant="body2">{selectLabel}</Typography>
        {indicators}
      </Box>
      {/* Node box & contents */}
      <div ref={dataNodeContentRef} style={{ width: '100%' }}>
        <NodeBackground width={width ?? 0} isSelected={selected} />
        <Stack
          direction="row"
          justifyContent="center"
          spacing={1}
          // fixed height here to support the svg background
          sx={{ height: '76px', paddingX: 4 }}
        >
          {inputsFiltered.map((component) => {
            return (
              <DataHandleWithLabel
                key={component.handleId}
                id={component.handleId}
                name={component.name}
                position={Position.Top}
                isConnected={component.isConnected}
                handleStyle={{ top: '-5px' }}
              />
            );
          })}
          {inputsFiltered.length > 0 && outputs.length > 0 && (
            <Box sx={{ paddingY: 2, height: '100%', zIndex: 2 }}>
              <Divider orientation="vertical" />
            </Box>
          )}
          {outputs.map((component) => {
            return (
              <DataHandleWithLabel
                key={component.handleId}
                id={component.handleId}
                name={component.name}
                position={Position.Bottom}
                handleStyle={{ bottom: '-5px' }}
                isConnected={component.isConnected}
              />
            );
          })}
        </Stack>
      </div>
      <Box position="absolute" top="50%" left={40}>
        <Handle
          id={toTaskDependencyHandleId('source', id)}
          position={Position.Right}
          type="source"
          isConnectable={false}
          style={{ visibility: 'hidden' }}
        />
        <Handle
          id={toTaskDependencyHandleId('target', id)}
          position={Position.Right}
          type="source"
          isConnectable={false}
          style={{ visibility: 'hidden' }}
        />
      </Box>
    </Stack>
  );
};
